import {
  Box,
  Button,
  Dialog,
  Backdrop,
  MenuItem,
  TextField,
  FormLabel,
  DialogTitle,
  FormControl,
  DialogActions,
  DialogContent,
  CircularProgress,
  DialogContentText,
} from '@mui/material';
import * as yup from 'yup';
import { useSnackbar } from 'notistack';
import { AutocompleteMultiById } from '..';
import apiService from 'library/api-service';
import { EFFORT_STATUS_TYPE_ENUM } from 'enums';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import loaderLogoSrc from 'assets/images/idecon-loader-logo.png';
import { EffortContactItem, EffortStatusContactItem } from 'library/types';

type CreateEffortStatusContactFormParams = {
  status: number | '';
  toContacts: number[];
  ccContacts: number[];
};

export type CreateEffortStatusContactPostFormParams = Omit<CreateEffortStatusContactFormParams, 'status'> & {
  status: number;
};

type CreateEffortStatusContactDialogProps = {
  open: boolean;
  handleClose: () => void;
  refresh: () => Promise<void>;
  effortContacts: EffortContactItem[];
  effortStatusContacts: EffortStatusContactItem[];
  translateFunction: (effortStatusType: number) => string | undefined;
};

const CreateEffortStatusContactDialog = ({
  open,
  handleClose: closeDialog,
  refresh,
  effortContacts,
  effortStatusContacts,
  translateFunction,
}: CreateEffortStatusContactDialogProps) => {
  // other hooks
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();

  // useState hooks
  const [loading, setLoading] = useState(false);

  // dynamic variables
  const STATUS_OPTIONS = useMemo(
    () => [
      {
        value: EFFORT_STATUS_TYPE_ENUM.IN_SALES,
        label: translateFunction(EFFORT_STATUS_TYPE_ENUM.IN_SALES),
      },
      {
        value: EFFORT_STATUS_TYPE_ENUM.PENDING_CUSTOMER_APPROVAL,
        label: translateFunction(EFFORT_STATUS_TYPE_ENUM.PENDING_CUSTOMER_APPROVAL),
      },
      {
        value: EFFORT_STATUS_TYPE_ENUM.APPROVED,
        label: translateFunction(EFFORT_STATUS_TYPE_ENUM.APPROVED),
      },
      {
        value: EFFORT_STATUS_TYPE_ENUM.REJECTED,
        label: translateFunction(EFFORT_STATUS_TYPE_ENUM.REJECTED),
      },
      {
        value: EFFORT_STATUS_TYPE_ENUM.WORK_STARTED,
        label: translateFunction(EFFORT_STATUS_TYPE_ENUM.WORK_STARTED),
      },
      {
        value: EFFORT_STATUS_TYPE_ENUM.TESTING_BY_CUSTOMER,
        label: translateFunction(EFFORT_STATUS_TYPE_ENUM.TESTING_BY_CUSTOMER),
      },
      {
        value: EFFORT_STATUS_TYPE_ENUM.WORK_COMPLETED,
        label: translateFunction(EFFORT_STATUS_TYPE_ENUM.WORK_COMPLETED),
      },
      {
        value: EFFORT_STATUS_TYPE_ENUM.INVOICED,
        label: translateFunction(EFFORT_STATUS_TYPE_ENUM.INVOICED),
      },
    ],
    [translateFunction]
  );

  // form validations & form state
  const schema = yup.object({
    status: yup
      .number()
      .typeError(intl.formatMessage({ id: 'validations.required', defaultMessage: 'Bu alan boş bırakılamaz' }))
      .required(intl.formatMessage({ id: 'validations.required', defaultMessage: 'Bu alan boş bırakılamaz' })),
    toContacts: yup
      .array()
      .of(
        yup
          .number()
          .required(intl.formatMessage({ id: 'validations.required', defaultMessage: 'Bu alan boş bırakılamaz' }))
      )
      .min(
        1,
        intl.formatMessage({
          id: 'validations.effortContacts.minOne',
          defaultMessage: 'En az 1 efor muhatabı seçilmelidir.',
        })
      )
      .required(),
  });

  const { reset, control, handleSubmit } = useForm<CreateEffortStatusContactFormParams>({
    defaultValues: { status: '', toContacts: [], ccContacts: [] },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    // @ts-ignore
    resolver: yupResolver(schema),
    shouldFocusError: true,
  });

  // sync helper functions
  const idsToOptions = useCallback(
    (ids: number[]) => effortContacts.filter((c) => ids.includes(c.id)),
    [effortContacts]
  );

  const optionsToIds = (opts: EffortContactItem[]) => opts.map((o) => o.id);

  const handleClose = () => {
    closeDialog();
    reset();
  };

  // async helper functions
  const onSubmit = async (data: CreateEffortStatusContactFormParams) => {
    const postData: CreateEffortStatusContactPostFormParams = {
      ...data,
      status: Number(data.status),
    };

    try {
      setLoading(true);

      const response = await apiService.createEffortStatusContact(postData);

      if (response.isSuccess) {
        enqueueSnackbar(
          <FormattedMessage
            id="effortStatusContacts.createEffortStatusContactCompleted"
            defaultMessage="Efor Statü Muhatabı oluşturma işlemi başarıyla tamamlandı."
          />,
          { variant: 'success' }
        );
        handleClose();
        refresh();
      }
    } catch {
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog disableRestoreFocus open={open} onClose={handleClose}>
      <DialogTitle>
        <FormattedMessage
          id="effortStatusContacts.createEffortStatusContact"
          defaultMessage="Efor Statü Muhatabı Oluştur"
        />
      </DialogTitle>
      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <DialogContent sx={{ display: 'grid', gap: 2, width: '100%' }}>
          <DialogContentText>
            <FormattedMessage
              id="effortStatusContacts.createEffortStatusContactText"
              defaultMessage="İlgili alanları girip devam ederek yeni bir Efor Statü Muhatabı oluşturabilirsiniz."
            />
          </DialogContentText>
          <FormControl>
            <FormLabel htmlFor="status">
              <FormattedMessage id="effortTracing.status" defaultMessage="Statü" />
            </FormLabel>
            <Controller
              name="status"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  select
                  fullWidth
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}>
                  {STATUS_OPTIONS.filter(
                    (opt) =>
                      !effortStatusContacts.map((effortStatusContact) => effortStatusContact.status).includes(opt.value)
                  ).map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </FormControl>
          <FormControl>
            <FormLabel htmlFor="to">TO</FormLabel>
            <Controller
              name="toContacts"
              control={control}
              render={({ field, fieldState }) => (
                <AutocompleteMultiById
                  options={effortContacts}
                  valueOptions={idsToOptions(field.value)}
                  onChangeOptions={(opts) => field.onChange(optionsToIds(opts))}
                  errorText={fieldState.error?.message}
                />
              )}
            />
          </FormControl>
          <FormControl>
            <FormLabel htmlFor="cc">CC</FormLabel>
            <Controller
              name="ccContacts"
              control={control}
              render={({ field, fieldState }) => (
                <AutocompleteMultiById
                  options={effortContacts}
                  valueOptions={idsToOptions(field.value)}
                  onChangeOptions={(opts) => field.onChange(optionsToIds(opts))}
                  errorText={fieldState.error?.message}
                />
              )}
            />
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ pb: 3, px: 3 }}>
          <Button onClick={handleClose}>
            <FormattedMessage id="forgotPassword.cancel" defaultMessage="Vazgeç" />
          </Button>
          <Button variant="contained" type="submit">
            <FormattedMessage id="forgotPassword.continue" defaultMessage="Devam" />
          </Button>
        </DialogActions>
      </Box>
      <Backdrop
        sx={{ display: 'flex', flexDirection: 'column', color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}>
        <img src={loaderLogoSrc} alt="loader-logo" />
        <CircularProgress color="inherit" />
      </Backdrop>
    </Dialog>
  );
};

export default CreateEffortStatusContactDialog;
